import { UseFormSetError } from 'react-hook-form';
import { IClientLogContext } from 'src/contexts/ClientLogContext';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import { isUniqueWithin } from 'src/utils/validation';
import {
  PosClientConfig,
  SaleClient,
  TicketBarcode,
  TicketBarcodeResponse,
} from 'src/WebApiController';

export type BarcodeForm = {
  isBulkUpload: boolean;
  barcodes: TicketBarcode[];
};

export type TicketBarcodeWithEntityId = TicketBarcode & {
  entityId: number;
};

export type BulkBarcodeForm = {
  isBulkUpload: boolean;
  barcodes: TicketBarcodeWithEntityId[];
};

export const barcodeDictToArray = (allBarcodes: {
  [key: string]: TicketBarcodeResponse[];
}): TicketBarcodeWithEntityId[] => {
  const barcodes: TicketBarcodeWithEntityId[] = [];

  Object.keys(allBarcodes).forEach((key) => {
    const ticketBarcodes = allBarcodes[key];
    ticketBarcodes.forEach((barcode) => {
      barcodes.push({
        ticketId: barcode.ticketId,
        barcode: barcode.barcode ?? '',
        section: barcode.section,
        row: barcode.row,
        seat: barcode.seat,
        entityId: Number(key),
      });
    });
  });

  return barcodes;
};

export const barcodeArrayToDict = (
  barcodes: TicketBarcodeWithEntityId[]
): { [key: string]: TicketBarcode[] } => {
  const allBarcodes: { [key: string]: TicketBarcode[] } = {};

  barcodes.forEach((barcode) => {
    const entityId = barcode.entityId.toString();
    if (!allBarcodes[entityId]) {
      allBarcodes[entityId] = [];
    }
    allBarcodes[entityId].push({
      ticketId: barcode.ticketId,
      barcode: barcode.barcode,
      section: barcode.section,
      row: barcode.row,
      seat: barcode.seat,
    });
  });
  return allBarcodes;
};

export const checkDuplicateBarcodes = async (
  entityId: number | undefined,
  barcodes: TicketBarcode[],
  activeAccountWebClientConfig: PosClientConfig,
  alreadyReportedUsedBarcodes: string[],
  setAlreadyReportedUsedBarcodes: (barcodes: string[]) => void,
  onDuplicateBarcode: (index: number) => void,
  onBeforeCheck?: () => void,
  onAfterCheck?: () => void,
  showErrorDialog?: IClientLogContext['showErrorDialog'],
  viagVirtId?: string
): Promise<boolean | null> => {
  return tryInvokeApi(
    async () => {
      onBeforeCheck?.();

      if (!barcodes) {
        return false;
      }

      const existingBarcodes = await new SaleClient(
        activeAccountWebClientConfig
      ).getBarcodesAlreadyInUse(viagVirtId, barcodes);

      if (existingBarcodes) {
        // Get all the used barcodes that have not yet been reported
        const unreportedBarCodes = existingBarcodes.filter(
          (b) => !alreadyReportedUsedBarcodes.includes(b.barcode)
        );
        if (unreportedBarCodes.length > 0) {
          // We're reporting the dup warnings, so save it so we don't report on it again
          // We want to set all of it (not just the latest unreported barcodes)
          setAlreadyReportedUsedBarcodes(
            existingBarcodes.map((tb) => tb.barcode)
          );

          let hasErrors = false;
          existingBarcodes.forEach((tb) => {
            const index = barcodes.findIndex((b) => b.ticketId === tb.ticketId);

            if (index >= 0) {
              hasErrors = true;
              onDuplicateBarcode(index);
            }
          });

          return hasErrors;
        }
      }

      return false;
    },
    (error) => {
      showErrorDialog?.('SaleClient.getBarcodesAlreadyInUse', error, {
        trackErrorData: {
          saleId: entityId,
          barcodes,
        },
      });
    },
    () => onAfterCheck?.()
  );
};

export const checkBarcodeErrors = (
  isBulkUpload: boolean,
  barcodes: TicketBarcode[],
  ticketCnt: number,
  setError: UseFormSetError<BarcodeForm>,
  requiredMsg: string,
  missingMsg: string,
  extraMsg: string,
  duplicateMsg: string
) => {
  let hasErrors = false;
  const barcodesProvided =
    barcodes?.map((b) => b.barcode).filter((b) => b?.length) ?? [];
  if (isBulkUpload) {
    const barcodesProvided =
      barcodes?.map((b) => b.barcode).filter((b) => b?.length) ?? [];
    if (barcodesProvided.length < ticketCnt) {
      hasErrors = true;
      setError(`barcodes`, { message: missingMsg as string }); // for the bulk-edit inputs
    } else if (barcodesProvided.length > ticketCnt) {
      hasErrors = true;
      setError(`barcodes`, { message: extraMsg as string }); // for the bulk-edit inputs
    }
  }

  barcodes?.forEach((b, i) => {
    if (!b.barcode) {
      hasErrors = true;
      setError(`barcodes.${i}.barcode`, { message: requiredMsg });
    } else if (!isUniqueWithin(b.barcode, barcodesProvided, true)) {
      hasErrors = true;
      if (isBulkUpload) {
        setError(`barcodes`, { message: duplicateMsg }); // for the bulk-edit inputs
      }
      setError(`barcodes.${i}.barcode`, { message: duplicateMsg });
    }
  });
  return hasErrors;
};

export const checkBulkBarcodeErrors = (
  isBulkUpload: boolean,
  barcodes: TicketBarcodeWithEntityId[],
  setError: UseFormSetError<BarcodeForm>,
  duplicateMsg: string,
  requiredMsg: string,
  allowPartialWithinEntity?: boolean
) => {
  let hasErrors = false;
  const barcodesProvided =
    barcodes?.map((b) => b.barcode).filter((b) => b?.length) ?? [];

  barcodes?.forEach((b, i) => {
    if (!isUniqueWithin(b.barcode, barcodesProvided, true)) {
      hasErrors = true;
      if (isBulkUpload) {
        setError(`barcodes`, { message: duplicateMsg }); // for the bulk-edit inputs
      }
      setError(`barcodes.${i}.barcode`, { message: duplicateMsg });
    }
  });

  // Don't allow submit if there are no barcodes provided for all entities
  if (barcodesProvided.length === 0) {
    hasErrors = true;
    setError(`barcodes`, { message: requiredMsg as string }); // for the bulk-edit inputs
    barcodes?.forEach((b, i) => {
      setError(`barcodes.${i}.barcode`, { message: requiredMsg });
    });
  }

  if (!allowPartialWithinEntity) {
    const barcodeIndexesByEntityId = barcodes.reduce(
      (acc, b, index) => {
        if (!acc[b.entityId]) {
          acc[b.entityId] = [];
        }
        acc[b.entityId].push(index);
        return acc;
      },
      {} as { [key: string]: number[] }
    );

    Object.values(barcodeIndexesByEntityId).forEach((barcodeIndexes) => {
      const barcodesProvided = barcodeIndexes
        .map((i) => barcodes[i].barcode)
        .filter((b) => b?.length);

      // Either it's empty or all barcodes are provided
      if (
        barcodesProvided.length > 0 &&
        barcodesProvided.length !== barcodeIndexes.length
      ) {
        hasErrors = true;
        barcodeIndexes.forEach((index) => {
          setError(`barcodes.${index}.barcode`, { message: requiredMsg });
        });
      }
    });
  }
  return hasErrors;
};
